/**
 * Edge-swipe-back for the installed app.
 *
 * ONLY runs in standalone (Home Screen) mode, and that restriction is the point: in a normal Safari
 * tab iOS already owns the left-edge swipe and performs its own back, so adding ours would pop TWO
 * entries per gesture. Installed, there is no browser chrome and no system gesture — the swipe does
 * nothing at all, which is the gap this fills.
 *
 * Drives the transform imperatively rather than through React state: a touchmove fires at display
 * rate, and re-rendering the whole shell on each one is exactly how a gesture ends up feeling
 * worse than no gesture.
 */
import * as React from 'react';

/** How close to the left edge a drag must start. Matches iOS's own edge region. */
const EDGE_PX = 24;
/** Horizontal distance that commits the back once released. */
const COMMIT_PX = 72;
/** Past this much vertical movement the gesture is a scroll, not a swipe. */
const VERTICAL_ABORT_PX = 18;

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone === true;
}

/**
 * True when the touch began inside something that scrolls horizontally and is not already at its
 * left end — a wide data table, a chip strip. Swiping there means "scroll the table", and hijacking
 * it would make the table unusable near the edge.
 */
function startedInsideHorizontalScroller(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el) {
    if (el.scrollWidth > el.clientWidth + 1 && el.scrollLeft > 0) return true;
    el = el.parentElement;
  }
  return false;
}

export interface SwipeBackOptions {
  /** Element translated as the drag progresses — normally the scrolling <main>. */
  surfaceRef: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  onBack: () => void;
}

export function useSwipeBack({ surfaceRef, enabled, onBack }: SwipeBackOptions): void {
  // Held in a ref so the latest callback is used without re-binding listeners each render.
  const onBackRef = React.useRef(onBack);
  onBackRef.current = onBack;

  React.useEffect(() => {
    if (!enabled) return;
    const surface = surfaceRef.current;
    if (!surface) return;
    if (!isStandaloneDisplay()) return;

    let tracking = false;
    let startX = 0;
    let startY = 0;
    let dx = 0;

    const paint = (offset: number, animate: boolean) => {
      surface.style.transition = animate ? 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)' : '';
      surface.style.transform = offset === 0 ? '' : `translateX(${offset}px)`;
    };

    const reset = (animate: boolean) => {
      tracking = false;
      dx = 0;
      paint(0, animate);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX > EDGE_PX) return;
      if (startedInsideHorizontalScroller(e.target)) return;
      tracking = true;
      startX = t.clientX;
      startY = t.clientY;
      dx = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking) return;
      const t = e.touches[0];
      const moveY = Math.abs(t.clientY - startY);
      dx = t.clientX - startX;
      if (moveY > VERTICAL_ABORT_PX && moveY > Math.abs(dx)) {
        reset(true); // it was a scroll after all
        return;
      }
      if (dx <= 0) {
        paint(0, false);
        return;
      }
      // Resistance past the commit point: the surface keeps responding but stops tracking the
      // finger 1:1, which is what signals "let go now" without any text.
      const eased = dx > COMMIT_PX ? COMMIT_PX + (dx - COMMIT_PX) * 0.35 : dx;
      paint(eased, false);
      // Only once we are clearly horizontal — otherwise this eats vertical scrolling.
      if (e.cancelable && Math.abs(dx) > moveY) e.preventDefault();
    };

    const onTouchEnd = () => {
      if (!tracking) return;
      const commit = dx >= COMMIT_PX;
      reset(true);
      if (commit) onBackRef.current();
    };

    // Passive false on move only — we may need preventDefault to stop the page rubber-banding
    // sideways mid-gesture.
    surface.addEventListener('touchstart', onTouchStart, { passive: true });
    surface.addEventListener('touchmove', onTouchMove, { passive: false });
    surface.addEventListener('touchend', onTouchEnd, { passive: true });
    surface.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      surface.removeEventListener('touchstart', onTouchStart);
      surface.removeEventListener('touchmove', onTouchMove);
      surface.removeEventListener('touchend', onTouchEnd);
      surface.removeEventListener('touchcancel', onTouchEnd);
      // A gesture interrupted by an unmount must not leave the surface translated off-screen.
      surface.style.transition = '';
      surface.style.transform = '';
    };
  }, [enabled, surfaceRef]);
}
